import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";
import { DEFAULT_PAGE_SIZE, useWindowedRows } from "@/hooks/useWindowedRows";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
    ContentTile,
    Grid,
    ContentGridSkeleton,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Text,
    VideoEditDialog,
    VideoPreviewDialog,
} from "@athena/ui";
import { Search } from "lucide-react";
import {
    DeleteVideoAssetDocument,
    SetVideoAssetTagsDocument,
    TagOptionsDocument,
    UpdateVideoAssetDocument,
    VideoAssetsCountDocument,
    VideoAssetsDocument,
    type VideoAssetsQuery,
} from "@/graphql/media.generated";
import { OrganizationOptionsDocument } from "@/graphql/organizations.generated";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { useVideoPlayback } from "@/hooks/useVideoPlayback";
import { DeleteDialog } from "@/components/DeleteDialog";
import { OrganizationSelect } from "@/components/OrganizationSelect";

/** Every status a filter may select, plus the default. */
const STATUSES = [
    "READY",
    "PROCESSING",
    "UPLOADING",
    "FAILED",
] as const;

const ALL = "__all__";

export function VideosPanel() {
    const { t } = useTranslation();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        orgFilter,
        setOrgFilter,
    ] = useState<string>(ALL);
    const [
        statusFilter,
        setStatusFilter,
    ] = useState<string>(ALL);
    /** The asset being edited, held whole so the dialog shows what it is about to change. */
    const [
        editing,
        setEditing,
    ] = useState<{
        id: string;
        title: string;
        description: string;
        tagIds: string[];
        usedInCount: number;
    } | null>(null);
    /** The asset being previewed, or null when the dialog is closed. */
    const [
        previewing,
        setPreviewing,
    ] = useState<{
        id: string;
        title: string;
        organizationName: string;
        tags: { name: string }[];
        description: string;
        createdAt: string;
    } | null>(null);
    const [
        deleteVideo,
        setDeleteVideo,
    ] = useState<{ id: string; title: string } | null>(null);

    const { isPlatformAdmin, organizationId, loading: meLoading } = usePlatformAdmin();

    /*
     * Windowed, with every narrowing done by the server.
     *
     * This panel used to hold the whole library and filter it in the browser. Organization, tag,
     * status and the text search all moved into `videoAssetFilterClauses`, which is what makes the
     * window safe: filtering a window in the browser searches whatever has scrolled and reports
     * nothing for a video sitting in the database.
     *
     * Tag filtering was the last one to move, and it is an `EXISTS` over the lessons that present
     * the asset — a join would return the same video once per matching lesson and make `limit`
     * count duplicates.
     */
    const settledSearch = useDebouncedValue(search);
    const queryTerm = search.trim() === "" ? "" : settledSearch.trim();
    const listVariables = useMemo(
        () => ({
            organizationId: orgFilter !== ALL ? orgFilter : organizationId,
            ...(queryTerm ? { search: queryTerm } : {}),
            ...(statusFilter !== ALL
                ? { status: statusFilter as VideoAssetsQuery["videoAssets"][number]["status"] }
                : {}),
        }),
        [
            organizationId,
            orgFilter,
            queryTerm,
            statusFilter,
        ],
    );

    const {
        rows: allAssets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
    } = useWindowedRows<VideoAssetsQuery["videoAssets"][number]>(
        VideoAssetsDocument,
        (d) => (d as VideoAssetsQuery | undefined)?.videoAssets,
        {
            variables: listVariables,
            skip: meLoading || (!isPlatformAdmin && !organizationId),
        },
    );

    const { data: countData } = useQuery(VideoAssetsCountDocument, {
        skip: meLoading || (!isPlatformAdmin && !organizationId),
    });
    const total = countData?.videoAssetsCount;

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(allAssets, { hasMore, loadMore, pageSize: DEFAULT_PAGE_SIZE });

    const sentinelRef = useInfiniteScroll(loadMore, hasMore);
    const preview = useVideoPlayback(previewing?.id ?? null);

    // Only a platform admin sees videos from more than one organization, so only they need the
    // filter — for anyone else every row is their own org and the control would be inert.
    const { data: orgsData } = useQuery(OrganizationOptionsDocument, { skip: !isPlatformAdmin });

    const { data: tagsData } = useQuery(TagOptionsDocument);
    const [setVideoAssetTags] = useMutation(SetVideoAssetTagsDocument, {
        /*
         * The mutation answers with a bare `Tag` list — nothing in it names the asset the tags
         * belong to, so Apollo has no field to write them to and the grid kept its old chips until
         * a reload. `updateVideoAsset` needs none of this because it returns the asset itself.
         */
        update: (cache, { data }, { variables }) => {
            if (!data || !variables) return;
            cache.modify({
                id: cache.identify({ __typename: "VideoAsset", id: variables.videoAssetId }),
                fields: {
                    tags: (_existing, { toReference }) => data.setVideoAssetTags.map((tag) => toReference(tag, true)),
                },
            });
        },
        onError: (error) => {
            Sentry.captureException(error);
            toast.error(error.message || t("admin.data.videoSaveFailed"));
        },
    });

    const [updateVideoAsset] = useMutation(UpdateVideoAssetDocument, {
        onError: (error) => {
            Sentry.captureException(error);
            toast.error(error.message || t("admin.data.videoSaveFailed"));
        },
        onCompleted: () => setEditing(null),
    });

    const assets = view.rows;

    const orgNames = useMemo(
        () =>
            new Map(
                (orgsData?.organizations ?? []).map((o) => [
                    o.id,
                    o.name,
                ]),
            ),
        [orgsData],
    );

    /*
     * One rule left in the browser, and only this one.
     *
     * Everything else is a server predicate now. A failure nothing uses is deleted by the server
     * while answering the very query that returned it, so this response is the last one that will
     * ever carry it — a rule about *this response* rather than about the data, which is why it
     * cannot move into SQL.
     */
    const filtered = useMemo(() => {
        // A failure a lesson still presents is the opposite case and stays: a course is pointing at
        // a video that cannot play, and somebody has to go fix that.
        return assets.filter((a) => !(a.status === "FAILED" && a.usedInCount === 0));
    }, [assets]);

    /**
     * Title, description and tags save together, because the dialog presents them as one edit — a
     * rename that landed while a tag change silently vanished is the worse half of a partial
     * success. Tags are the video's own (ADR 0014), so they save whether or not a lesson uses it.
     */
    const saveEdit = async () => {
        if (!editing?.title.trim()) return;
        await Promise.all([
            updateVideoAsset({
                variables: {
                    id: editing.id,
                    // Description is sent every time, blank included: an empty one clears it, which
                    // is a real edit rather than an omission.
                    input: { title: editing.title.trim(), description: editing.description.trim() },
                },
            }),
            setVideoAssetTags({ variables: { videoAssetId: editing.id, tagIds: editing.tagIds } }),
        ]);
    };

    const [
        deleteVideoAsset,
        { loading: deleting },
    ] = useMutation(DeleteVideoAssetDocument, {
        update: (cache, { data: mutationData }) => {
            if (!mutationData) return;
            cache.evict({ id: cache.identify({ __typename: "VideoAsset", id: mutationData.deleteVideoAsset.id }) });
            cache.gc();
        },
        // The server's own message, matching the two mutations above. Delete refuses an asset a
        // lesson still presents and names the count — "Cannot delete: 2 content item(s) still
        // reference this video. Detach them first." — which is the only actionable thing here, and a
        // generic "could not delete" throws it away. That case is reachable from this grid by design:
        // a failed asset a lesson uses is exactly what stays listed.
        onError: (error) => {
            Sentry.captureException(error);
            toast.error(error.message || t("admin.lms.deleteVideoFailed"));
        },
    });

    const handleDelete = async () => {
        if (!deleteVideo) return;
        try {
            await deleteVideoAsset({ variables: { id: deleteVideo.id } });
            setDeleteVideo(null);
        } catch {
            // Reported via the mutation's onError above.
        }
    };
    const nameOf = (a: { title?: string | null; originalFilename?: string | null }): string =>
        a.title?.trim() || a.originalFilename || t("admin.lms.video");

    if (!organizationId && !isPlatformAdmin && !loading && !meLoading) {
        return <Text className="text-muted-foreground">{t("admin.data.noOrganization")}</Text>;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-56 flex-1">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                        placeholder={t("admin.data.searchVideos")}
                        aria-label={t("admin.data.searchVideos")}
                        className="pl-9"
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {isPlatformAdmin && (
                    <OrganizationSelect
                        value={orgFilter}
                        onValueChange={setOrgFilter}
                        className="w-48"
                        aria-label="Filter by organization"
                        leadingOptions={[{ value: ALL, label: "All organizations" }]}
                    />
                )}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" aria-label="Filter by status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All statuses</SelectItem>
                        {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                                {s}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <ListViewControls
                view={view}
                noun="videos"
                count={total === undefined ? undefined : `${view.from}–${view.to} of ${total}`}
            />

            {loading ? (
                <ContentGridSkeleton />
            ) : filtered.length === 0 ? (
                <Text muted className="flex h-32 items-center justify-center text-sm">
                    {assets.length === 0 ? t("admin.lms.noUploadsYet") : "No videos match these filters."}
                </Text>
            ) : (
                <Grid cols={3} gap="md" className="w-full items-stretch">
                    {filtered.map((asset) => {
                        const displayName = asset.title?.trim() || asset.originalFilename || t("admin.lms.video");
                        const orgLabel = asset.organizationId === "global-library" ? "Global Library" : "";
                        return (
                            <div key={asset.id} className="flex flex-col items-center gap-2">
                                <ContentTile
                                    title={nameOf(asset)}
                                    posterUrl={asset.posterUrl}
                                    durationSeconds={asset.durationSeconds}
                                    onPlayClicked={
                                        asset.status === "FAILED"
                                            ? undefined
                                            : () =>
                                                  setPreviewing({
                                                      id: asset.id,
                                                      title: displayName,
                                                      description: asset.description ?? "",
                                                      organizationName: orgLabel,
                                                      tags: asset.tags,
                                                      createdAt: asset.createdAt,
                                                  })
                                    }
                                    processingStatus={asset.status}
                                    organizationName={orgNames.get(asset.organizationId) ?? ""}
                                    onDeleteClicked={() => setDeleteVideo({ id: asset.id, title: nameOf(asset) })}
                                    onEditClicked={() =>
                                        setEditing({
                                            id: asset.id,
                                            title: asset.title ?? "",
                                            description: asset.description ?? "",
                                            tagIds: asset.tags.map((tag) => tag.id),
                                            usedInCount: asset.usedInCount,
                                        })
                                    }
                                    tags={asset.tags}
                                />
                                {/*
                                 * Only reachable for a failure a lesson still uses — the unused ones are
                                 * filtered out above. Says what to do about it, because the operator
                                 * cannot delete this one: `deleteVideoAsset` refuses while content
                                 * references it, and detaching is what makes it collectable.
                                 */}
                                {asset.status === "FAILED" && (
                                    <Text role="alert" className="text-destructive px-1 text-xs">
                                        Processing failed, so this cannot be played. It is still used in{" "}
                                        {asset.usedInCount === 1 ? "1 lesson" : `${asset.usedInCount} lessons`} — swap
                                        in a working video there, and this upload is cleaned up on its own.
                                    </Text>
                                )}
                            </div>
                        );
                    })}
                </Grid>
            )}

            {/* Fetches the next page as it nears the viewport; removed at the end of the list. */}
            {view.mode === "scroll" && hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
            {view.mode === "scroll" && !hasMore && (
                <div ref={sentinelRef} aria-hidden className="text-muted-foreground h-px py-4 text-center text-sm">
                    You've reached the end of the list! ✨
                </div>
            )}
            {loadingMore && <div className="text-muted-foreground py-4 text-center text-sm">Loading more…</div>}

            <VideoPreviewDialog
                open={previewing !== null}
                title={previewing?.title ?? ""}
                description={previewing?.description ?? ""}
                playback={preview.playback}
                organizationName={previewing?.organizationName}
                tags={previewing?.tags}
                createdAt={previewing?.createdAt}
                loading={preview.loading}
                errorMessage={preview.errorMessage}
                onClose={() => setPreviewing(null)}
            />
            <VideoEditDialog
                open={editing !== null}
                onClose={() => setEditing(null)}
                heading={`Editing ${editing?.title}`}
                // Explicit, because `titleLabel` otherwise falls back to `heading` — which now
                // carries the title itself, so the field's accessible name would change on every
                // keystroke and read as "Editing Intro" rather than naming the field.
                titleLabel={t("admin.data.columnTitle")}
                title={editing?.title ?? ""}
                onTitleChange={(next) => setEditing((prev) => (prev ? { ...prev, title: next } : prev))}
                description={editing?.description ?? ""}
                onDescriptionChange={(next) => setEditing((prev) => (prev ? { ...prev, description: next } : prev))}
                descriptionLabel={t("admin.data.columnDescription")}
                tagOptions={tagsData?.tags ?? []}
                tagIds={editing?.tagIds ?? []}
                onTagIdsChange={(next) => setEditing((prev) => (prev ? { ...prev, tagIds: next } : prev))}
                usedInCount={editing?.usedInCount ?? 0}
                onSave={() => void saveEdit()}
            />
            <DeleteDialog
                open={!!deleteVideo}
                onOpenChange={(o) => {
                    if (!o) setDeleteVideo(null);
                }}
                title="Delete video"
                deleteTarget={deleteVideo?.title}
                onConfirm={handleDelete}
                loading={deleting}
            />
        </div>
    );
}
